import useFetch from "hooks/useFetch";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Row, Col, Form, Spinner } from "react-bootstrap";
import LittleCourseCard from "components/littleCourseCard/LittleCourseCard";

const MyCourses = () => {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const { get, isLoading } = useFetch();
  const [userCourses, setUserCourses] = useState([]);
  const [allLearningPaths, setAllLearningPaths] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const { t } = useTranslation();

  const [filterMode, setFilterMode] = useState("all");
  const [selectedLearningPathId, setSelectedLearningPathId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [subscriptions, setSubscriptions] = useState([]);

  const getAllCurrentUserSubscriptions = async () => {
    const response = await get(`/users/${currentUser.id}/subscriptions`);
    if (response) {
      setSubscriptions(response);
      const allCourses = response.flatMap(
        (subscription) => subscription.learning_path.courses
      );
      const uniqueCourses = Array.from(
        new Map(allCourses.map((course) => [course.id, course])).values()
      );
      setUserCourses(uniqueCourses);

      const learningPaths = response
        .map((subscription) => subscription.learning_path)
        .filter(Boolean);
      const uniqueLearningPaths = Array.from(
        new Map(
          learningPaths.map((learningPath) => [learningPath.id, learningPath])
        ).values()
      ).map((learningPath) => ({
        id: learningPath.id,
        title: learningPath.title,
      }));
      setAllLearningPaths(uniqueLearningPaths);
    }
  };

  const getAllCategories = async () => {
    const response = await get("/categories?include_learning_paths=true");
    if (response) {
      const uniqueCategories = Array.from(
        new Map(response.map((category) => [category.id, category])).values()
      );
      setAllCategories(uniqueCategories);
    }
  };

  useEffect(() => {
    if (currentUser) {
      getAllCurrentUserSubscriptions();
    }
  }, [currentUser]);

  useEffect(() => {
    getAllCategories();
  }, []);

  const handleFilterModeChange = async (event) => {
    const mode = event.target.value;
    setFilterMode(mode);
    setSelectedLearningPathId("");
    setSelectedCategoryId("");

    if (mode === "all") {
      await getAllCurrentUserSubscriptions();
    } else if (mode === "byCategory") {
      setUserCourses([]);
    }
  };

  const handleLearningPathChange = async (event) => {
    const learningPathId = event.target.value;
    setSelectedLearningPathId(learningPathId);

    const subscription = subscriptions.find(
      (s) => String(s.learning_path.id) === String(learningPathId)
    );
    const courses = subscription ? subscription.learning_path.courses : [];
    const uniqueCourses = Array.from(
      new Map(courses.map((c) => [c.id, c])).values()
    );
    setUserCourses(uniqueCourses);
  };

  const handleCategoryChange = (event) => {
    const categoryId = event.target.value;
    setSelectedCategoryId(categoryId);
  
    const category = allCategories.find(
      (c) => String(c.id) === String(categoryId)
    );
    if (!category) {
      setUserCourses([]);
      return;
    }
  
    const categoryLearningPathIds = new Set(
      (category.learning_paths || []).map((lp) => lp.id)
    );
  
    const courses = subscriptions
      .filter(
        (s) =>
          s.learning_path &&
          categoryLearningPathIds.has(s.learning_path.id)
      )
      .flatMap((s) => s.learning_path.courses || []);
  
    const uniqueCourses = Array.from(
      new Map(courses.map((c) => [c.id, c])).values()
    );
  
    setUserCourses(uniqueCourses);
  };

  const modeOptions = useMemo(
    () => [
      { value: "all", label: t("common:show_all") },
      { value: "byLearningPath", label: t("common:by_learning_path") },
      { value: "byCategory", label: t("common:by_category") },
    ],
    [t]
  );

  return (
    <div className="MyCourses">
      <div className="page-title-section text-center">
        <h4 className="page-title">{t("navigation:my_courses")}</h4>
      </div>

      <Row className="justify-content-center">
        <Col lg={10} className="mb-3">
          <Form>
            <Form.Row className="align-items-center">
              <Col xs={12} md={4} className="mb-2 mb-md-0">
                <Form.Label className="mb-1 font-weight-bold">
                  {t("common:filters_label")}
                </Form.Label>
                <Form.Control
                  as="select"
                  custom
                  value={filterMode}
                  onChange={handleFilterModeChange}
                >
                  {modeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Col>

              {filterMode === "byLearningPath" && (
                <Col xs={12} md={4} className="mb-2 mb-md-0">
                  <Form.Label className="mb-1 font-weight-bold">
                    {t("common:select_learning_path")}
                  </Form.Label>
                  <Form.Control
                    as="select"
                    custom
                    value={selectedLearningPathId}
                    onChange={handleLearningPathChange}
                  >
                    <option value="" disabled>
                      {allLearningPaths.length
                        ? t("common:choose_one")
                        : t("common:none_available")}
                    </option>
                    {allLearningPaths.map((learningPath) => (
                      <option key={learningPath.id} value={learningPath.id}>
                        {learningPath.title}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
              )}

              {filterMode === "byCategory" && (
                <Col xs={12} md={4} className="mb-2 mb-md-0">
                  <Form.Label className="mb-1 font-weight-bold">
                    {t("common:select_category")}
                  </Form.Label>
                  <Form.Control
                    as="select"
                    custom
                    value={selectedCategoryId}
                    onChange={handleCategoryChange}
                  >
                    <option value="" disabled>
                      {allCategories.length
                        ? t("common:choose_one")
                        : t("common:none_available")}
                    </option>
                    {allCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.title}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
              )}
            </Form.Row>
          </Form>
        </Col>
      </Row>

      <Row className="d-flex justify-content-center">
        {isLoading && (
          <Spinner animation="border" role="status" className="mt-3">
            <span className="sr-only">Loading...</span>
          </Spinner>
        )}

        {!isLoading &&
          userCourses.length > 0 &&
          userCourses.map((course) => (
            <LittleCourseCard key={course.id} course={course} />
          ))}

        {!isLoading && userCourses.length === 0 && (
          <p className="text-center mt-3">{t("no_subscriptions")}</p>
        )}
      </Row>
    </div>
  );
};

export default MyCourses;
